import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';

import BaseAuthenticator from 'ember-simple-auth/authenticators/base';
import { makeRequest } from 'solomon-next-reporting-ui/utils/requests';

import type StoreService from '@ember-data/store';
import type CurrentUserService from 'solomon-next-reporting-ui/services/current-user';
import type SessionService from 'solomon-next-reporting-ui/services/session';
import type { AuthenticatedData } from 'solomon-next-reporting-ui/services/session';
import type SolomonConfigService from 'solomon-next-reporting-ui/services/solomon-config';

// Step one of the handshake answers with HTTP 200 and this shape when the
// deployment has two-factor authentication switched on. qrCodeUrl and
// twoFactorSecret only ever come back the first time a collaborator signs in:
// the API generates the secret, saves it against the collaborator and never
// hands it out again.
export interface TwoFactorChallenge {
  requiresTwoFactor: true;
  tmpAuthToken: string;
  qrCodeUrl?: string;
  twoFactorSecret?: string;
}

// ember-simple-auth only leaves the session unauthenticated if authenticate()
// rejects, so the challenge is signalled by throwing rather than returning.
export class TwoFactorRequiredError extends Error {
  tmpAuthToken: string;

  qrCodeUrl: string;

  twoFactorSecret: string;

  constructor(challenge: TwoFactorChallenge) {
    super('Two factor authentication required');

    this.name = 'TwoFactorRequiredError';
    this.tmpAuthToken = challenge.tmpAuthToken;
    this.qrCodeUrl = challenge.qrCodeUrl || '';
    this.twoFactorSecret = challenge.twoFactorSecret || '';
  }
}

// The access token document the API stores also carries the tmpAuthToken it was
// issued with.
type AccessTokenResponse = AuthenticatedData & { tmpAuthToken?: string };

type LoginResponse = TwoFactorChallenge | AccessTokenResponse;

function isTwoFactorChallenge(response: LoginResponse): response is TwoFactorChallenge {
  return (response as TwoFactorChallenge).requiresTwoFactor === true;
}

export default class ApplicationAuthenticator extends BaseAuthenticator {
  @service declare solomonConfig: SolomonConfigService;

  @service declare currentUser: CurrentUserService;

  @service declare session: SessionService;

  @service declare store: StoreService;

  restore(data: Data) {
    return new Promise((resolve, reject) => {
      if (isEmpty(data['id'])) {
        reject();
      } else {
        resolve(data);
      }
    });
  }

  async authenticate(email: string, password: string, tmpAuthToken?: string, twoFactorCode?: string) {
    // Step two of the handshake. The password is deliberately left out - it does
    // not need to survive the trip to the authenticator app - but the email must
    // be sent: the collaborator login runs
    // findOne({ where: normalizeCredentials(credentials) }) as an active-user
    // check before it reaches the two-factor branch, and without an email that
    // lookup degenerates into "find any collaborator" and fails LOGIN_FAILED
    // against an inactive one.
    const data = tmpAuthToken ? { email, tmpAuthToken, twoFactorCode } : { email, password };

    const host = this.solomonConfig.config.apis.solomonApi.host;

    const response: LoginResponse = await makeRequest(`${host}/api/collaborators/login`, data);

    if (isTwoFactorChallenge(response)) {
      throw new TwoFactorRequiredError(response);
    }

    if (!response.id || !response.userId) {
      throw new Error('Unauthorized');
    }

    // Drop the handshake token so it is not persisted into local storage along
    // with the session.
    const token: AccessTokenResponse = { ...response };

    delete token.tmpAuthToken;

    return token;
  }
}
